import React, { FC, useEffect, useRef, useState } from 'react';
import CryptoJS from 'crypto-js';
import queryString from 'query-string';
import manifestAPI from 'api/manifestAPI';
import textAPI from 'api/textAPI';
import { DEFAULT_TOEIC_YEAR } from 'config/defaultYear';
import ToolLayout from 'tools/ToolLayout';
import {
  btnDisabled,
  btnRow,
  card,
  chipStyle,
  errorBox,
  hint,
  label,
  primaryBtn,
  privacy,
  secondaryBtn,
  textArea,
  warnBox,
} from 'tools/toolStyles';
import { encodeWav, revokeUrl } from 'tools/TextToSpeech/audioUtils';
import { getEngineForModel } from 'tools/TextToSpeech/engines';
import { PresetVoice } from 'tools/TextToSpeech/engines/types';
import { getModelById, MODELS, ModelEntry } from 'tools/TextToSpeech/modelRegistry';
import DictationAudioPlayer from './DictationAudioPlayer';
import { DictationMode, DictationScore, DictationTag, scoreDictation } from './scoring';
import { createToeicDictationSource } from './toeic';

type SourceMode = 'upload' | 'tts' | 'toeic';

const SPEEDS = [0.5, 0.75, 1, 1.25, 1.5];
const PART_ITEM_COUNTS: { [key: number]: number } = { 1: 6, 2: 25, 3: 13, 4: 10 };

function getWin(): any {
  const g: any = typeof global !== 'undefined' ? global : {};
  return g.window || g;
}

function positiveInt(value: any, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

const tabs: Array<{ id: SourceMode; label: string }> = [
  { id: 'upload', label: 'Upload audio' },
  { id: 'tts', label: 'Văn bản + TTS' },
  { id: 'toeic', label: 'Kho TOEIC' },
];

const inputStyle: any = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '11px 12px',
  minHeight: 44,
  borderRadius: 8,
  border: '1px solid #cfd8dc',
  fontSize: 15,
  background: '#fff',
};

const grid: any = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(145px, 1fr))',
  gap: 10,
  marginBottom: 12,
};

const resultGrid: any = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 7,
  lineHeight: 1.35,
};

const scoreBox: any = {
  background: '#e3f2fd',
  color: '#0d47a1',
  borderRadius: 10,
  padding: '12px 14px',
  marginBottom: 12,
  fontWeight: 700,
};

const transcriptBox: any = {
  whiteSpace: 'pre-wrap',
  lineHeight: 1.65,
  background: '#f7f9fa',
  border: '1px solid #e0e0e0',
  borderRadius: 8,
  padding: 12,
  margin: '8px 0 0',
};

function wordStyle(tag: DictationTag): any {
  const colors: { [key in DictationTag]: { background: string; color: string } } = {
    ok: { background: '#e8f5e9', color: '#1b5e20' },
    near: { background: '#fff8e1', color: '#8d6e00' },
    wrong: { background: '#ffebee', color: '#b71c1c' },
    missing: { background: '#fce4ec', color: '#880e4f' },
    extra: { background: '#ede7f6', color: '#4527a0' },
  };
  return {
    ...colors[tag],
    borderRadius: 6,
    padding: '5px 7px',
    fontSize: 14,
  };
}

function wordLabel(expected: string, heard: string, tag: DictationTag): string {
  if (tag === 'ok') return expected;
  if (tag === 'missing') return `∅ → ${expected}`;
  if (tag === 'extra') return `+ ${heard}`;
  return `${heard} → ${expected}`;
}

const Dictation: FC = () => {
  const win = getWin();
  const initialQueryRef = useRef<any>(
    queryString.parse(String((win.location && win.location.search) || ''))
  );
  const initialQuery = initialQueryRef.current;
  const requestedSource = String(initialQuery.source || 'upload');
  const initialSource: SourceMode =
    requestedSource === 'tts' || requestedSource === 'toeic' || requestedSource === 'upload'
      ? (requestedSource as SourceMode)
      : 'upload';

  const [sourceMode, setSourceMode] = useState<SourceMode>(initialSource);
  const [configError, setConfigError] = useState(
    requestedSource === initialSource ? '' : 'Nguồn trong liên kết không hợp lệ. Hãy chọn lại.'
  );
  const [expectedText, setExpectedText] = useState('');
  const [sourceTitle, setSourceTitle] = useState('');
  const [audioSrc, setAudioSrc] = useState<string | null>(null);
  const [browserSpeechReady, setBrowserSpeechReady] = useState(false);
  const [answer, setAnswer] = useState('');
  const [scoreMode, setScoreMode] = useState<DictationMode>('flexible');
  const [result, setResult] = useState<DictationScore | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [practiceError, setPracticeError] = useState('');
  const ownedUrlRef = useRef<string | null>(null);

  const [uploadFile, setUploadFile] = useState<any>(null);
  const [uploadTranscript, setUploadTranscript] = useState('');

  const [ttsText, setTtsText] = useState('');
  const [ttsLang, setTtsLang] = useState<'en' | 'vi'>('en');
  const [ttsModelId, setTtsModelId] = useState('webspeech');
  const [ttsVoices, setTtsVoices] = useState<PresetVoice[]>([]);
  const [ttsVoiceId, setTtsVoiceId] = useState('');
  const [ttsSpeed, setTtsSpeed] = useState(1);
  const [ttsConsent, setTtsConsent] = useState(false);
  const [ttsBusy, setTtsBusy] = useState(false);
  const [ttsStatus, setTtsStatus] = useState('');
  const [ttsError, setTtsError] = useState('');
  const [browserSpeaking, setBrowserSpeaking] = useState(false);
  const [browserRepeat, setBrowserRepeat] = useState(false);
  const browserRepeatRef = useRef(false);
  const ttsAbortRef = useRef({ aborted: false });

  const initialPart = positiveInt(initialQuery.part, 4);
  const initialItem = positiveInt(initialQuery.item, 1);
  const [toeicYears, setToeicYears] = useState<string[]>([]);
  const [toeicTests, setToeicTests] = useState<any[]>([]);
  const [toeicYear, setToeicYear] = useState(String(initialQuery.year || DEFAULT_TOEIC_YEAR));
  const [toeicTest, setToeicTest] = useState(positiveInt(initialQuery.test, 1));
  const [toeicPart, setToeicPart] = useState(initialPart >= 1 && initialPart <= 4 ? initialPart : 4);
  const [toeicItem, setToeicItem] = useState(initialItem);
  const [toeicLoading, setToeicLoading] = useState(false);
  const [toeicError, setToeicError] = useState(
    initialSource === 'toeic' && (initialPart < 1 || initialPart > 4)
      ? 'Part trong liên kết phải từ 1 đến 4.'
      : ''
  );
  const toeicAutoLoadRef = useRef(initialSource === 'toeic');

  const currentModel = (getModelById(ttsModelId) || getModelById('webspeech')) as ModelEntry;
  const availableModels = MODELS.filter(
    (model) => !model.comingSoon && model.langs.indexOf(ttsLang) >= 0
  );
  const selectedTest = toeicTests.find((entry) => Number(entry.test) === toeicTest);
  const availableParts = selectedTest
    ? (selectedTest.parts || []).map(Number).filter((part: number) => part >= 1 && part <= 4)
    : [1, 2, 3, 4];

  const replaceAudio = (url: string | null, owned: boolean) => {
    if (ownedUrlRef.current) revokeUrl(ownedUrlRef.current);
    ownedUrlRef.current = owned && url ? url : null;
    setAudioSrc(url);
  };

  const cancelTts = () => {
    ttsAbortRef.current.aborted = true;
    const model = (getModelById(ttsModelId) || getModelById('webspeech')) as ModelEntry;
    const engine = getEngineForModel(model);
    if (engine.cancel) engine.cancel();
    setTtsBusy(false);
    setBrowserSpeaking(false);
    setTtsStatus('Đã dừng.');
  };

  const resetPractice = () => {
    setAnswer('');
    setResult(null);
    setRevealed(false);
    setPracticeError('');
  };

  const clearWorkspace = () => {
    cancelTts();
    replaceAudio(null, false);
    setExpectedText('');
    setSourceTitle('');
    setBrowserSpeechReady(false);
    resetPractice();
  };

  const prepareWorkspace = (title: string, transcript: string, url: string | null, owned: boolean) => {
    replaceAudio(url, owned);
    setExpectedText(transcript.trim());
    setSourceTitle(title);
    setBrowserSpeechReady(false);
    resetPractice();
  };

  useEffect(() => {
    browserRepeatRef.current = browserRepeat;
  }, [browserRepeat]);

  useEffect(() => {
    return () => {
      ttsAbortRef.current.aborted = true;
      if (ownedUrlRef.current) revokeUrl(ownedUrlRef.current);
    };
  }, []);

  useEffect(() => {
    if (sourceMode !== 'tts' || ttsModelId !== 'webspeech') return;
    const model = getModelById('webspeech') as ModelEntry;
    const engine = getEngineForModel(model);
    engine
      .ensureLoaded('webspeech')
      .then(() => engine.listVoices('webspeech'))
      .then((voices) => {
        setTtsVoices(voices);
        const preferred = voices.find((voice) => voice.lang === ttsLang) || voices[0];
        if (preferred) setTtsVoiceId(preferred.id);
      })
      .catch(() => setTtsVoices([]));
  }, [sourceMode, ttsModelId, ttsLang]);

  useEffect(() => {
    if (sourceMode !== 'toeic') return;
    let cancelled = false;
    setToeicLoading(true);
    manifestAPI
      .getRoot()
      .then((root: any) => {
        if (cancelled) return;
        const years = ((root && root.years) || []).map((year: any) => String(year));
        setToeicYears(years);
      })
      .catch(() => {
        if (!cancelled) setToeicError('Không tải được danh sách năm TOEIC. Hãy thử lại.');
      })
      .finally(() => {
        if (!cancelled) setToeicLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceMode]);

  useEffect(() => {
    if (sourceMode !== 'toeic' || !toeicYear) return;
    let cancelled = false;
    setToeicLoading(true);
    manifestAPI
      .getYear(toeicYear)
      .then((manifest: any) => {
        if (cancelled) return;
        const tests = (manifest && manifest.tests) || [];
        setToeicTests(tests);
        setToeicError('');
      })
      .catch(() => {
        if (!cancelled) {
          setToeicTests([]);
          setToeicError(`Không tải được manifest TOEIC ${toeicYear}.`);
        }
      })
      .finally(() => {
        if (!cancelled) setToeicLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [sourceMode, toeicYear]);

  useEffect(() => {
    if (sourceMode !== 'toeic' || !toeicTests.length) return;
    if (!selectedTest) {
      setToeicError(`Không tìm thấy Test ${toeicTest} trong năm ${toeicYear}.`);
    }
  }, [sourceMode, toeicTests, selectedTest, toeicTest, toeicYear]);

  const loadToeic = async () => {
    setToeicError('');
    if (!selectedTest) {
      setToeicError('Test đã chọn không có trong manifest.');
      return;
    }
    if (availableParts.indexOf(toeicPart) < 0) {
      setToeicError(`Test này không có Part ${toeicPart}.`);
      return;
    }
    if (toeicItem < 1 || toeicItem > (PART_ITEM_COUNTS[toeicPart] || 0)) {
      setToeicError(`Part ${toeicPart} chỉ có ${PART_ITEM_COUNTS[toeicPart]} bài.`);
      return;
    }

    setToeicLoading(true);
    try {
      const encrypted = await textAPI.get(toeicYear, toeicTest, toeicPart);
      const plain = CryptoJS.AES.decrypt(encrypted, 'thieumao').toString(CryptoJS.enc.Utf8);
      if (!plain) throw new Error('Không giải mã được dữ liệu TOEIC.');
      const items = JSON.parse(plain);
      const source = createToeicDictationSource(
        toeicYear,
        toeicTest,
        toeicPart,
        toeicItem,
        items
      );
      prepareWorkspace(source.title, source.transcript, source.audioSrc, false);
      if (win.history && win.history.replaceState) {
        const search = queryString.stringify({
          source: 'toeic',
          year: toeicYear,
          test: toeicTest,
          part: toeicPart,
          item: toeicItem,
        });
        win.history.replaceState({}, '', `/tools/dictation?${search}`);
      }
    } catch (error) {
      setToeicError((error && (error as any).message) || 'Không tải được bài TOEIC.');
    } finally {
      setToeicLoading(false);
    }
  };

  useEffect(() => {
    if (
      sourceMode === 'toeic' &&
      toeicAutoLoadRef.current &&
      toeicTests.length > 0 &&
      selectedTest
    ) {
      toeicAutoLoadRef.current = false;
      loadToeic();
    }
    // The initial deep link should load exactly once after its manifest arrives.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceMode, toeicTests]);

  const beginUpload = () => {
    setConfigError('');
    if (!uploadFile) {
      setConfigError('Hãy chọn một file audio.');
      return;
    }
    if (!String(uploadFile.type || '').startsWith('audio/')) {
      setConfigError('File đã chọn không phải định dạng audio được trình duyệt nhận diện.');
      return;
    }
    if (!uploadTranscript.trim()) {
      setConfigError('Hãy dán transcript đáp án trước khi bắt đầu.');
      return;
    }
    const url = win.URL.createObjectURL(uploadFile);
    prepareWorkspace(uploadFile.name || 'Audio của bạn', uploadTranscript, url, true);
  };

  const prepareTts = async () => {
    const text = ttsText.trim();
    setTtsError('');
    if (!text) {
      setTtsError('Hãy nhập văn bản dùng làm transcript.');
      return;
    }
    if (currentModel.needsConsent && !ttsConsent) {
      setTtsError(`Hãy xác nhận tải model khoảng ${currentModel.sizeMb}MB.`);
      return;
    }

    const engine = getEngineForModel(currentModel);
    ttsAbortRef.current = { aborted: false };
    setTtsBusy(true);
    setTtsStatus('Đang chuẩn bị giọng…');
    replaceAudio(null, false);
    setExpectedText('');
    setSourceTitle('');
    setBrowserSpeechReady(false);
    resetPractice();
    try {
      if (!engine.isReady(currentModel.id)) {
        await engine.ensureLoaded(currentModel.id, (progress) => {
          const percent = typeof progress.progress === 'number' ? ` ${Math.round(progress.progress * 100)}%` : '';
          setTtsStatus(`${progress.status}${percent}`);
        });
      }
      const voices = await engine.listVoices(currentModel.id);
      setTtsVoices(voices);
      const selectedVoice = voices.find((voice) => voice.id === ttsVoiceId) ||
        voices.find((voice) => voice.lang === ttsLang) ||
        voices[0];
      if (selectedVoice) setTtsVoiceId(selectedVoice.id);

      if (currentModel.engine === 'webspeech') {
        setExpectedText(text);
        setSourceTitle(`TTS trình duyệt · ${ttsLang.toUpperCase()}`);
        setBrowserSpeechReady(true);
        resetPractice();
        setTtsStatus('Sẵn sàng phát bằng giọng trình duyệt.');
        return;
      }

      setTtsStatus('Đang tạo audio…');
      const synthesized = await engine.synthesize(currentModel.id, {
        text,
        voiceId: selectedVoice && selectedVoice.id,
        speed: 1,
        lang: ttsLang,
        signal: ttsAbortRef.current,
        onProgress: (progress) => setTtsStatus(progress.status),
      });
      if (ttsAbortRef.current.aborted) throw new Error('Đã hủy.');
      if (!synthesized.pcm || !synthesized.pcm.length) {
        throw new Error('Model không trả về audio.');
      }
      const blob = encodeWav(synthesized.pcm, synthesized.sampleRate);
      const url = win.URL.createObjectURL(blob);
      prepareWorkspace(`${currentModel.label} · ${ttsLang.toUpperCase()}`, text, url, true);
      setTtsStatus('Đã tạo audio.');
    } catch (error) {
      setTtsStatus('');
      setTtsError((error && (error as any).message) || 'Không tạo được audio TTS.');
    } finally {
      setTtsBusy(false);
    }
  };

  const playBrowserSpeech = async () => {
    if (!browserSpeechReady || !expectedText) return;
    const model = getModelById('webspeech') as ModelEntry;
    const engine = getEngineForModel(model);
    ttsAbortRef.current = { aborted: false };
    setBrowserSpeaking(true);
    setTtsError('');
    try {
      do {
        await engine.synthesize('webspeech', {
          text: expectedText,
          voiceId: ttsVoiceId,
          speed: ttsSpeed,
          lang: ttsLang,
          signal: ttsAbortRef.current,
        });
      } while (browserRepeatRef.current && !ttsAbortRef.current.aborted);
    } catch (error) {
      if (!ttsAbortRef.current.aborted) {
        setTtsError((error && (error as any).message) || 'Không phát được giọng trình duyệt.');
      }
    } finally {
      setBrowserSpeaking(false);
    }
  };

  const grade = () => {
    setPracticeError('');
    if (!expectedText) {
      setPracticeError('Hãy chuẩn bị một bài dictation trước.');
      return;
    }
    if (!answer.trim()) {
      setPracticeError('Hãy chép nội dung bạn nghe được trước khi chấm.');
      return;
    }
    setResult(scoreDictation(expectedText, answer, scoreMode));
    setRevealed(true);
  };

  const changeSource = (next: SourceMode) => {
    if (next === sourceMode) return;
    clearWorkspace();
    setConfigError('');
    setToeicError('');
    setTtsError('');
    setSourceMode(next);
  };

  return (
    <ToolLayout title="Luyện nghe chép chính tả — Dictation" wide showBackToToeic>
      <p style={privacy}>
        Audio, transcript và bài làm được xử lý trên trình duyệt của bạn, không upload lên server.
      </p>

      <div style={card}>
        <span style={label}>1. Chọn nguồn bài nghe</span>
        <div style={btnRow} role="tablist" aria-label="Nguồn dictation">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={sourceMode === tab.id}
              style={chipStyle(sourceMode === tab.id)}
              onClick={() => changeSource(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {sourceMode === 'upload' ? (
          <div data-testid="upload-source">
            <label style={label} htmlFor="dictation-audio">File audio</label>
            <input
              id="dictation-audio"
              style={{ ...inputStyle, padding: 8, marginBottom: 12 }}
              type="file"
              accept="audio/*"
              onChange={(event: any) => setUploadFile(event.target.files && event.target.files[0])}
            />
            <label style={label} htmlFor="upload-transcript">Transcript đáp án</label>
            <textarea
              id="upload-transcript"
              style={textArea}
              rows={5}
              value={uploadTranscript}
              placeholder="Dán nội dung chính xác của audio…"
              onChange={(event: any) => setUploadTranscript(event.target.value)}
            />
            <div style={{ ...btnRow, marginTop: 12, marginBottom: 0 }}>
              <button type="button" style={primaryBtn} onClick={beginUpload}>Bắt đầu chép</button>
            </div>
          </div>
        ) : null}

        {sourceMode === 'tts' ? (
          <div data-testid="tts-source">
            <label style={label} htmlFor="tts-text">Văn bản dùng làm transcript</label>
            <textarea
              id="tts-text"
              style={textArea}
              rows={5}
              value={ttsText}
              placeholder="Nhập tiếng Anh hoặc tiếng Việt…"
              onChange={(event: any) => setTtsText(event.target.value)}
            />
            <div style={{ ...grid, marginTop: 12 }}>
              <label>
                <span style={label}>Ngôn ngữ</span>
                <select
                  aria-label="Ngôn ngữ TTS"
                  style={inputStyle}
                  value={ttsLang}
                  onChange={(event: any) => {
                    const next = event.target.value as 'en' | 'vi';
                    setTtsLang(next);
                    if ((currentModel.langs || []).indexOf(next) < 0) setTtsModelId('webspeech');
                    setTtsConsent(false);
                  }}
                >
                  <option value="en">English</option>
                  <option value="vi">Tiếng Việt</option>
                </select>
              </label>
              <label>
                <span style={label}>Engine / model</span>
                <select
                  aria-label="Model TTS"
                  style={inputStyle}
                  value={ttsModelId}
                  onChange={(event: any) => {
                    setTtsModelId(event.target.value);
                    setTtsVoices([]);
                    setTtsVoiceId('');
                    setTtsConsent(false);
                  }}
                >
                  {availableModels.map((model) => (
                    <option key={model.id} value={model.id}>
                      {model.label} · {model.sizeMb}MB
                    </option>
                  ))}
                </select>
              </label>
              {ttsVoices.length ? (
                <label>
                  <span style={label}>Giọng</span>
                  <select
                    aria-label="Giọng TTS"
                    style={inputStyle}
                    value={ttsVoiceId}
                    onChange={(event: any) => setTtsVoiceId(event.target.value)}
                  >
                    {ttsVoices.map((voice) => (
                      <option key={voice.id} value={voice.id}>{voice.label}</option>
                    ))}
                  </select>
                </label>
              ) : null}
            </div>
            <p style={hint}>
              {currentModel.subtitle} · License: {currentModel.license}. Model AI được cache theo cơ chế TTS hiện có.
            </p>
            {currentModel.needsConsent ? (
              <label style={{ ...warnBox, display: 'block' }}>
                <input
                  type="checkbox"
                  checked={ttsConsent}
                  onChange={(event: any) => setTtsConsent(event.target.checked)}
                />{' '}
                Tôi đồng ý tải khoảng {currentModel.sizeMb}MB dữ liệu model về trình duyệt.
              </label>
            ) : null}
            <div style={{ ...btnRow, marginBottom: 0 }}>
              <button
                type="button"
                style={ttsBusy ? btnDisabled(primaryBtn) : primaryBtn}
                disabled={ttsBusy}
                onClick={prepareTts}
              >
                {currentModel.engine === 'webspeech' ? 'Chuẩn bị bài' : 'Tạo audio & bắt đầu'}
              </button>
              {ttsBusy ? (
                <button type="button" style={secondaryBtn} onClick={cancelTts}>Dừng</button>
              ) : null}
            </div>
            {ttsStatus ? <p style={{ ...hint, marginTop: 10 }}>{ttsStatus}</p> : null}
            {ttsError ? <p style={errorBox}>{ttsError}</p> : null}
          </div>
        ) : null}

        {sourceMode === 'toeic' ? (
          <div data-testid="toeic-source">
            <div style={grid}>
              <label>
                <span style={label}>Năm</span>
                <select
                  aria-label="Năm TOEIC"
                  style={inputStyle}
                  value={toeicYear}
                  onChange={(event: any) => {
                    setToeicYear(event.target.value);
                    setToeicError('');
                  }}
                >
                  {(toeicYears.length ? toeicYears : [toeicYear]).map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </label>
              <label>
                <span style={label}>Test</span>
                <select
                  aria-label="Test TOEIC"
                  style={inputStyle}
                  value={toeicTest}
                  onChange={(event: any) => {
                    setToeicTest(Number(event.target.value));
                    setToeicError('');
                  }}
                >
                  {(toeicTests.length ? toeicTests : [{ test: toeicTest }]).map((entry: any) => (
                    <option key={entry.test} value={entry.test}>Test {entry.test}</option>
                  ))}
                </select>
              </label>
              <label>
                <span style={label}>Part nghe</span>
                <select
                  aria-label="Part TOEIC"
                  style={inputStyle}
                  value={toeicPart}
                  onChange={(event: any) => {
                    setToeicPart(Number(event.target.value));
                    setToeicItem(1);
                    setToeicError('');
                  }}
                >
                  {(availableParts.length ? availableParts : [1, 2, 3, 4]).map((part: number) => (
                    <option key={part} value={part}>Part {part}</option>
                  ))}
                </select>
              </label>
              <label>
                <span style={label}>Bài</span>
                <input
                  aria-label="Số bài TOEIC"
                  style={inputStyle}
                  type="number"
                  min={1}
                  max={PART_ITEM_COUNTS[toeicPart]}
                  value={toeicItem}
                  onChange={(event: any) => setToeicItem(Number(event.target.value))}
                />
              </label>
            </div>
            <p style={hint}>
              Part {toeicPart} có {PART_ITEM_COUNTS[toeicPart]} bài. Transcript và audio được đọc từ kho ETSData hiện có.
            </p>
            <button
              type="button"
              style={toeicLoading ? btnDisabled(primaryBtn) : primaryBtn}
              disabled={toeicLoading}
              onClick={loadToeic}
            >
              {toeicLoading ? 'Đang tải…' : 'Mở bài TOEIC'}
            </button>
            {toeicError ? <p style={{ ...errorBox, marginTop: 12 }}>{toeicError}</p> : null}
          </div>
        ) : null}

        {configError ? <p style={{ ...errorBox, marginTop: 12 }}>{configError}</p> : null}
      </div>

      {expectedText ? (
        <>
          <div style={card}>
            <span style={label}>2. Nghe và chép</span>
            <p style={{ ...hint, marginBottom: 12 }}><strong>{sourceTitle}</strong></p>
            {audioSrc ? <DictationAudioPlayer src={audioSrc} /> : null}
            {browserSpeechReady ? (
              <div>
                <div style={btnRow}>
                  <button
                    type="button"
                    style={browserSpeaking ? btnDisabled(primaryBtn) : primaryBtn}
                    disabled={browserSpeaking}
                    onClick={playBrowserSpeech}
                  >
                    {browserSpeaking ? 'Đang phát…' : 'Phát giọng trình duyệt'}
                  </button>
                  {browserSpeaking ? (
                    <button type="button" style={secondaryBtn} onClick={cancelTts}>Dừng</button>
                  ) : null}
                  <label style={{ ...secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="checkbox"
                      checked={browserRepeat}
                      onChange={(event: any) => setBrowserRepeat(event.target.checked)}
                    />
                    Lặp toàn bài
                  </label>
                </div>
                <div style={btnRow}>
                  {SPEEDS.map((item) => (
                    <button
                      key={item}
                      type="button"
                      style={chipStyle(ttsSpeed === item)}
                      onClick={() => setTtsSpeed(item)}
                    >
                      {item === 1 ? '1.0' : item}×
                    </button>
                  ))}
                </div>
                <p style={hint}>Web Speech phát trực tiếp nên không hỗ trợ timeline hoặc đánh dấu A–B.</p>
              </div>
            ) : null}

            <label style={{ ...label, marginTop: 12 }} htmlFor="dictation-answer">Nội dung bạn nghe được</label>
            <textarea
              id="dictation-answer"
              style={{ ...textArea, minHeight: 150 }}
              rows={7}
              value={answer}
              placeholder="Chép lại ở đây. Transcript vẫn được ẩn cho tới khi bạn chấm hoặc xem đáp án."
              onChange={(event: any) => {
                setAnswer(event.target.value);
                setResult(null);
                setRevealed(false);
              }}
            />
          </div>

          <div style={card}>
            <span style={label}>3. Chấm bài</span>
            <div style={btnRow}>
              <button type="button" style={chipStyle(scoreMode === 'flexible')} onClick={() => setScoreMode('flexible')}>
                Linh hoạt
              </button>
              <button type="button" style={chipStyle(scoreMode === 'strict')} onClick={() => setScoreMode('strict')}>
                Nghiêm ngặt
              </button>
            </div>
            <p style={hint}>
              {scoreMode === 'flexible'
                ? 'Bỏ qua hoa/thường và dấu câu; lỗi chính tả gần đúng nhận nửa điểm.'
                : 'Giữ nguyên hoa/thường và dấu câu; chỉ token khớp hoàn toàn mới có điểm.'}
            </p>
            <div style={btnRow}>
              <button type="button" style={primaryBtn} onClick={grade}>Chấm bài</button>
              <button
                type="button"
                style={secondaryBtn}
                onClick={() => {
                  setResult(null);
                  setRevealed(true);
                  setPracticeError('');
                }}
              >
                Xem đáp án
              </button>
              <button type="button" style={secondaryBtn} onClick={resetPractice}>Làm lại</button>
            </div>
            {practiceError ? <p style={errorBox}>{practiceError}</p> : null}

            {result ? (
              <div data-testid="dictation-result">
                <div style={scoreBox}>
                  Điểm: {result.scorePct}% · {result.points}/{result.denominator} điểm từ · Đáp án{' '}
                  {result.expectedCount} từ / Bài làm {result.submittedCount} từ
                </div>
                <div style={resultGrid}>
                  {result.words.map((word, index) => (
                    <span key={`${index}-${word.expected}-${word.heard}`} style={wordStyle(word.tag)}>
                      {wordLabel(word.expected, word.heard, word.tag)}
                    </span>
                  ))}
                </div>
                <p style={{ ...hint, marginTop: 12, marginBottom: 0 }}>
                  Xanh: đúng · Vàng: gần đúng · Đỏ: sai/thiếu · Tím: thừa.
                </p>
              </div>
            ) : null}

            {revealed ? (
              <div>
                <span style={{ ...label, marginTop: 16 }}>Transcript đáp án</span>
                <p style={transcriptBox}>{expectedText}</p>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </ToolLayout>
  );
};

export default Dictation;
